// ----------------------------------------------
// IMPORTS
// ----------------------------------------------
import React, { useState, useEffect, useRef } from "react";
import "./Ai_solutions.css";
import { CDN_BASE_URL } from "../../config/cdn";

import * as THREE from "three";
import SpriteText from "three-spritetext";
import { useNavigate } from "react-router-dom";

import {
    Code2,
    Shield,
    Clock,
    CreditCard,
    DollarSign,
    TrendingUp,
    MessageSquare,
    CheckCircle,
    ArrowRight,
    Star,
    Quote,
    X
} from "lucide-react";


// (removed unused `theme` constant to satisfy linting rules)


// ----------------------------------------------
// 🔥 OPTION A — ALL DATA ARRAYS AT THE TOP
// ----------------------------------------------
const solutions = [
    {
        id: 1, icon: Code2, title: "Intelligent Medical Coding",
        description: "AI-powered coding engines automatically assign accurate ICD, CPT, and HCPCS codes by analyzing clinical documentation and physician notes.",
        benefits: ["Reduces coding errors & denials", "Improves claim accuracy", "Speeds up coding turnaround time"],
        image: `${CDN_BASE_URL}/images/ai-solutions/medical-coding.png`
    },
    {
        id: 2, icon: Shield, title: "Claim Scrubbing & Denial Prediction",
        description: "Machine learning models analyze claims before submission to detect potential errors and predict chances of denial.",
        benefits: ["Increases first-pass acceptance rate", "Saves time and revenue loss", "Reduces compliance risks"],
        image: `${CDN_BASE_URL}/images/ai-solutions/denial_prediction.png.png`
    },
    {
        id: 3, icon: Clock, title: "Automated Prior Authorization",
        description: "AI extracts required documentation and interacts with payer portals to speed up approval workflows.",
        benefits: ["60–80% reduced wait time", "Fewer manual phone calls & faxes", "Improved patient experience"],
        image: `${CDN_BASE_URL}/images/ai-solutions/Automated-Prior.png`
    },
    {
        id: 4, icon: CreditCard, title: "Patient Eligibility & Benefits Verification",
        description: "OCR + RPA bots automatically verify coverage and benefits across insurance providers.",
        benefits: ["Minimizes coverage-related denials", "Faster front-desk processing", "Accurate patient cost estimation"],
        image: `${CDN_BASE_URL}/images/ai-solutions/Patient-Eligibility.png`
    },
    {
        id: 5, icon: DollarSign, title: "Automated Payment Posting",
        description: "AI reads EOBs and ERAs and posts payments directly into billing systems.",
        benefits: ["90% faster posting speed", "Reduced manual errors", "Real-time reconciliation"],
        image: `${CDN_BASE_URL}/images/ai-solutions/Automated-Payment.png`
    },
    {
        id: 6, icon: TrendingUp, title: "Predictive Analytics & Revenue Insights",
        description: "AI dashboards analyze financial patterns to forecast revenue and identify leakage points.",
        benefits: ["Data-driven financial visibility", "Reduces AR days", "Improves profitability"],
        image: `${CDN_BASE_URL}/images/ai-solutions/Predictive-Analytics.png`
    },
    {
        id: 7, icon: MessageSquare, title: "AI Customer Support & Chatbots",
        description: "Automated patient support to answer billing queries, provide statement breakdowns, and collect payments.",
        benefits: ["24/7 support", "Reduces call center workload", "Faster payment conversions"],
        image: `${CDN_BASE_URL}/images/ai-solutions/Ai-customer.png`
    },
];


const testimonials = [
    {
        name: "Dr. Sarah Mitchell",
        role: "Chief Medical Officer",
        organization: "Metro Health System",
        image: "https://images.unsplash.com/photo-1758691463626-0ab959babe00?w=400&auto=format&fit=crop",
        quote: "The AI coding solution reduced our denial rate by 45% in just 3 months. The accuracy is remarkable and has saved our team countless hours.",
        rating: 5,
    },
    {
        name: "James Rodriguez",
        role: "Revenue Cycle Director",
        organization: "Riverside Medical Center",
        image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&auto=format&fit=crop",
        quote: "Automated prior authorization has been a game-changer. We've cut approval times by 75% and our patients are much happier.",
        rating: 5,
    },
    {
        name: "Linda Chen",
        role: "CFO",
        organization: "Summit Healthcare Group",
        image: "https://images.unsplash.com/photo-1676552055618-22ec8cde399a?w=400&auto=format&fit=crop",
        quote: "The predictive analytics platform identified revenue leakage points we didn't even know existed. We recovered over $2M in the first year.",
        rating: 5,
    },
];


const caseStudies = [
    {
        title: "Regional Hospital Network",
        challenge: "45% denial rate and 90+ day AR cycles",
        solution: "Implemented AI coding and claim scrubbing",
        results: [
            "Denial rate reduced to 12%",
            "AR days decreased from 92 to 38",
            "$4.2M additional revenue recovered annually",
        ],
        metric: "71% Improvement",
        colorClass: "ai-case-card-gradient-green",
    },
    {
        title: "Multi-Specialty Practice",
        challenge: "Manual prior authorization bottlenecks",
        solution: "Deployed automated PA workflow system",
        results: [
            "Authorization time reduced from 5 days to 8 hours",
            "Staff workload reduced by 60%",
            "Patient satisfaction increased by 40%",
        ],
        metric: "85% Faster",
        colorClass: "ai-case-card-gradient-blue",
    },
    {
        title: "Ambulatory Surgery Center",
        challenge: "Payment posting delays and errors",
        solution: "AI-powered payment automation",
        results: [
            "Payment posting time reduced by 92%",
            "Error rate dropped to under 1%",
            "Real-time reconciliation achieved",
        ],
        metric: "92% Time Saved",
        colorClass: "ai-case-card-gradient-purple",
    },
];


// ----------------------------------------------
// 🔥 NEW 3D HERO SECTION (REPLACES OLD HERO)
// ----------------------------------------------

const HeroSection = () => {
    const [openDemo, setOpenDemo] = useState(false);
    const [successPopup, setSuccessPopup] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        date: "",
        time: "",
    });

    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        camera.position.z = 35;

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        mount.appendChild(renderer.domElement);

        const aiGroup = new THREE.Group();
        scene.add(aiGroup);

        // Sphere
        const geometry = new THREE.IcosahedronGeometry(11, 1);
        const material = new THREE.MeshBasicMaterial({
            color: 0x3b82f6,
            wireframe: true,
            transparent: true,
            opacity: 0.15,
        });
        const sphere = new THREE.Mesh(geometry, material);
        aiGroup.add(sphere);

        // Floating icons
        const iconsGroup = new THREE.Group();
        aiGroup.add(iconsGroup);

        const icons = ["$", "⚕️", "🏥", "📄", "AI", "🛡️", "⚙️", "✔"];
        const sprites = [];

        for (let i = 0; i < 40; i++) {
            const sprite = new SpriteText(icons[Math.floor(Math.random() * icons.length)]);
            sprite.color = "#93c5fd";
            sprite.textHeight = 1.6 + Math.random();
            sprite.opacity = 0.75;

            const r = 16 + Math.random() * 18;
            const t = Math.random() * Math.PI * 2;
            const p = Math.acos(2 * Math.random() - 1);

            sprite.position.set(
                r * Math.sin(p) * Math.cos(t),
                r * Math.sin(p) * Math.sin(t),
                r * Math.cos(p)
            );

            sprite.userData = { baseY: sprite.position.y };
            iconsGroup.add(sprite);
            sprites.push(sprite);
        }

        // Animation
        let mouseX = 0;
        let mouseY = 0;
        let animationFrameId;

        const handleMouseMove = (e) => {
    mouseX = (e.clientX / window.innerWidth) * 2 - 1;
    mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
};

window.addEventListener("mousemove", handleMouseMove);
        const animate = () => {
    animationFrameId = requestAnimationFrame(animate);

    aiGroup.rotation.y += (mouseX * 0.2 - aiGroup.rotation.y) * 0.05;
    aiGroup.rotation.x += (mouseY * 0.2 - aiGroup.rotation.x) * 0.05;

    const t = Date.now() * 0.001;

    sprites.forEach((s, i) => {
        s.position.y = s.userData.baseY + Math.sin(t + i) * 0.45;
    });

    renderer.render(scene, camera);
};

        animate();

        const handleResize = () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
};

window.addEventListener("resize", handleResize);

        return () => {
    cancelAnimationFrame(animationFrameId);

    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("resize", handleResize);

    geometry.dispose();
    material.dispose();

    renderer.dispose();
    renderer.forceContextLoss();

    if (
        mount &&
        renderer.domElement &&
        mount.contains(renderer.domElement)
    ) {
        mount.removeChild(renderer.domElement);
    }
};
    }, []);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        setOpenDemo(false);
        setSuccessPopup(true);
        setTimeout(() => setSuccessPopup(false), 2500);
    };

    return (
        <section className="hero-section-3d">
            {/* 3D Canvas */}
            <div ref={mountRef} className="hero-3d-canvas" />

            {/* Overlay */}
            <div className="hero-3d-overlay" />

            {/* Text */}
            <div className="hero-3d-content">
                <h1>AI-Powered Revenue Cycle Management</h1>
                <p>
                    Transform your healthcare revenue cycle with advanced AI. Reduce denials,
                    accelerate payments, and maximize profitability.
                </p>

                <div className="hero-3d-buttons">
                    <button className="hero-primary-btn" onClick={() => setOpenDemo(true)}>
                        Schedule Demo <ArrowRight size={18} />
                    </button>
                    <button className="hero-outline-btn">Learn More</button>
                </div>
            </div>

            {/* Demo Modal */}
            {openDemo && (
                <div className="hero-modal-overlay">
                    <div className="hero-modal-box">
                        <button className="hero-modal-close" onClick={() => setOpenDemo(false)}>
                            <X size={20} />
                        </button>

                        <h3>Schedule a Demo</h3>

                        <form onSubmit={handleSubmit}>
                            <input name="name" placeholder="Full Name" required onChange={handleChange} />
                            <input name="email" placeholder="Email" required onChange={handleChange} />
                            <input type="date" name="date" required onChange={handleChange} />
                            <input type="time" name="time" required onChange={handleChange} />
                            <button type="submit" className="hero-submit-btn">Confirm</button>
                        </form>
                    </div>
                </div>
            )}

            {successPopup && (
                <div className="hero-success-popup">🎉 Request Sent Successfully!</div>
            )}
        </section>
    );
};


// ----------------------------------------------
// REMAINING SECTIONS — UNCHANGED
// ----------------------------------------------

const FilterTabs = ({ activeTab, setActiveTab }) => {
    const navigate = useNavigate();   // 👈 ADD THIS

    return (
        <section className="ai-tabs">
            <div className="ai-tabs-inner">
                {[
                    { id: "all", label: "All Solutions" },
                    { id: "coding", label: "Medical Coding" },
                    { id: "claims", label: "Claims Management" },
                    { id: "automation", label: "Automation" },
                    { id: "analytics", label: "Analytics & Support" },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        className={`ai-tab-button ${activeTab === tab.id ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}

                {/* 👇 ADD THIS BUTTON */}
                <button
                    className="ai-tab-button ai-tab-outline"
                    onClick={() => navigate("/our-products")}
                >
                    Our Products
                </button>
            </div>
        </section>
    );
};


const SolutionsSection = ({ filteredSolutions }) => (
    <section className="ai-section ai-solutions-section">
        <div className="ai-section-header">
            <h2>Comprehensive AI Solutions for Healthcare RCM</h2>
            <p>Leverage the power of artificial intelligence to streamline every aspect of your revenue cycle.</p>
        </div>

        <div className="ai-solutions-list">
            {filteredSolutions.map((s, index) => (
                <div key={s.id} className={`ai-solution-row ${index % 2 === 1 ? "reverse" : ""}`}>
                    <div className="ai-solution-text">
                        <div className="ai-solution-icon">
                            <s.icon size={32} />
                        </div>

                        <h3>{s.title}</h3>
                        <p className="ai-solution-description">{s.description}</p>

                        <div className="ai-solution-benefits">
                            {s.benefits.map((b, i) => (
                                <div key={i} className="ai-benefit-item">
                                    <CheckCircle size={18} />
                                    <span>{b}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="ai-solution-image-wrapper">
                        <div className="ai-solution-image-card">
                            <img src={s.image} alt={s.title} />
                            <div className="ai-solution-image-overlay"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);

const CaseStudiesSection = () => (
    <section className="ai-section ai-case-section">
        <div className="ai-section-header">
            <h2>Real Results from Real Healthcare Organizations</h2>
            <p>See how our AI solutions have transformed revenue cycles.</p>
        </div>

        <div className="ai-case-grid">
            {caseStudies.map((c, index) => (
                <div key={index} className="ai-case-card">
                    <div className={`ai-case-card-header ${c.colorClass}`}>
                        <div className="ai-case-metric">{c.metric}</div>
                        <h3>{c.title}</h3>
                    </div>

                    <div className="ai-case-card-body">
                        <div className="ai-case-block">
                            <p className="ai-case-label">Challenge:</p>
                            <p className="ai-case-text">{c.challenge}</p>
                        </div>

                        <div className="ai-case-block">
                            <p className="ai-case-label">Solution:</p>
                            <p className="ai-case-text">{c.solution}</p>
                        </div>

                        <div className="ai-case-block">
                            <p className="ai-case-label">Results:</p>
                            <ul className="ai-case-results">
                                {c.results.map((r, idx) => (
                                    <li key={idx}>
                                        <CheckCircle size={16} /> {r}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);


const StatsSection = () => (
    <section className="ai-stats-section">
        <div className="ai-stats-grid">
            {[
                { value: "95%", label: "Claim Accuracy" },
                { value: "80%", label: "Faster Processing" },
                { value: "60%", label: "Denial Reduction" },
                { value: "24/7", label: "Automated Support" },
            ].map((s, i) => (
                <div key={i} className="ai-stat-item">
                    <div className="ai-stat-value">{s.value}</div>
                    <div className="ai-stat-label">{s.label}</div>
                </div>
            ))}
        </div>
    </section>
);


const TestimonialsSection = () => (
    <section className="ai-section ai-testimonial-section">
        <div className="ai-section-header">
            <h2>Trusted by Healthcare Leaders</h2>
            <p>Hear what our clients have to say about their experience.</p>
        </div>

        <div className="ai-testimonial-grid">
            {testimonials.map((t, index) => (
                <div key={index} className="ai-testimonial-card">
                    <div className="ai-testimonial-stars">
                        {Array.from({ length: t.rating }).map((_, i) => (
                            <Star key={i} size={18} className="ai-star-icon" />
                        ))}
                    </div>

                    <Quote size={36} className="ai-quote-icon" />

                    <p className="ai-testimonial-quote">"{t.quote}"</p>

                    <div className="ai-testimonial-user">
                        <img src={t.image} className="ai-testimonial-avatar" alt={t.name} />

                        <div>
                            <p className="ai-testimonial-name">{t.name}</p>
                            <p className="ai-testimonial-role">{t.role}</p>
                            <p className="ai-testimonial-org">{t.organization}</p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);


const CTASection = () => (
    <section className="ai-cta-section">
        <div className="ai-cta-content">
            <h2>Ready to Transform Your Revenue Cycle?</h2>
            <p>
                See how our AI solutions can help you reduce costs, increase efficiency,
                and maximize revenue.
            </p>
        </div>
    </section>
);


// ----------------------------------------------
// MAIN PAGE
// ----------------------------------------------

const AiSolutions = () => {
    const [activeTab, setActiveTab] = useState("all");

    const filteredSolutions =
        activeTab === "all"
            ? solutions
            : solutions.filter((s) => {
                if (activeTab === "coding") return s.id === 1;
                if (activeTab === "claims") return s.id === 2;
                if (activeTab === "automation") return [3, 4, 5].includes(s.id);
                if (activeTab === "analytics") return [6, 7].includes(s.id);
                return true;
            });

    return (
        <div className="ai-page">
            <HeroSection />

            <FilterTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            <SolutionsSection filteredSolutions={filteredSolutions} />
            <CaseStudiesSection />
            <StatsSection />
            <TestimonialsSection />
            <CTASection />
        </div>
    );
};

export default AiSolutions;
